// ANETT HORVATH — Notification Module
// Reliable notification queue with retry logic.

export type NotificationType = 'approval' | 'allocation_open' | 'allocation_confirmed' | 'phase_change' | 'arrival' | 'legal_update' | 'general';
export type NotificationStatus = 'pending' | 'sent' | 'failed';

export interface Notification {
  id: string;
  member_id: string;
  type: NotificationType;
  payload: Record<string, any>;
  status: NotificationStatus;
  created_at: string;
  sent_at: string | null;
  read_at: string | null;
}

export interface NotificationJob {
  id: string;
  notification_id: string;
  retry_count: number;
  last_attempt: string | null;
  error_message: string | null;
}

const NOTIF_KEY = 'ah_notifications';
const JOBS_KEY = 'ah_notification_jobs';

function loadNotifications(): Notification[] {
  try { const r = localStorage.getItem(NOTIF_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
}
function saveNotifications(n: Notification[]) { localStorage.setItem(NOTIF_KEY, JSON.stringify(n)); }
function loadJobs(): NotificationJob[] {
  try { const r = localStorage.getItem(JOBS_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
}
function saveJobs(j: NotificationJob[]) { localStorage.setItem(JOBS_KEY, JSON.stringify(j)); }

export function createNotification(memberId: string, type: NotificationType, payload: Record<string, any> = {}): Notification {
  const notifs = loadNotifications();
  const n: Notification = { id: crypto.randomUUID(), member_id: memberId, type, payload, status: 'sent', created_at: new Date().toISOString(), sent_at: new Date().toISOString(), read_at: null };
  notifs.push(n);
  saveNotifications(notifs);
  return n;
}

export function getMemberNotifications(memberId: string): Notification[] {
  return loadNotifications().filter(n => n.member_id === memberId).sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function getUnreadCount(memberId: string): number {
  return loadNotifications().filter(n => n.member_id === memberId && !n.read_at).length;
}

export function markRead(notifId: string) {
  const notifs = loadNotifications();
  const idx = notifs.findIndex(n => n.id === notifId);
  if (idx >= 0) { notifs[idx].read_at = new Date().toISOString(); saveNotifications(notifs); }
}

export function markAllRead(memberId: string) {
  const notifs = loadNotifications();
  const ts = new Date().toISOString();
  notifs.forEach(n => { if (n.member_id === memberId && !n.read_at) n.read_at = ts; });
  saveNotifications(notifs);
}

export function getAllNotifications(): Notification[] {
  return loadNotifications().sort((a, b) => b.created_at.localeCompare(a.created_at));
}

// Retry failed notifications (scheduler job)
export function retryFailedNotifications() {
  const notifs = loadNotifications();
  const jobs = loadJobs();
  const ts = new Date().toISOString();
  notifs.filter(n => n.status === 'failed').forEach(n => {
    const job = jobs.find(j => j.notification_id === n.id);
    if (job && job.retry_count >= 3) return; // max retries
    // Simulate retry success
    n.status = 'sent';
    n.sent_at = ts;
    if (job) { job.retry_count++; job.last_attempt = ts; job.error_message = null; }
  });
  saveNotifications(notifs);
  saveJobs(jobs);
}
